import { from } from 'rxjs';
import { first } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {
    Router,
    CanActivateFn,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
} from '@angular/router';
import {
    signIn,
    signOut,
    confirmSignIn,
    resetPassword,
    confirmResetPassword,
    fetchAuthSession,
    fetchUserAttributes,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
    defaultStorage,
    sessionStorage as amplifySessionStorage,
} from 'aws-amplify/utils';

const REMEMBER_ME_KEY = 'lsi.rememberMe';

/**
 * Point Cognito's token store at localStorage (persists across browser
 * restarts) when "remember me" is on, or sessionStorage (cleared when the tab
 * closes) when it's off. Must run before signIn and again on every app
 * bootstrap so tokens are read back from the same store they were written to.
 * Defaults to the previously saved choice, treating "unset" as remembered to
 * preserve the legacy persistent behaviour for already-signed-in users.
 */
export const applyRememberMeStorage = (
    remember: boolean = localStorage.getItem(REMEMBER_ME_KEY) !== 'false',
): void => {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
        remember ? defaultStorage : amplifySessionStorage,
    );
};

@Injectable({ providedIn: 'root' })
export class AuthService {
    private router = inject(Router);

    logIn(login: string, password: string) {
        return signIn({ username: login, password });
    }

    /** Persist the choice and switch the token store before signing in. */
    setRememberMe(remember: boolean): void {
        localStorage.setItem(REMEMBER_ME_KEY, remember ? 'true' : 'false');
        applyRememberMeStorage(remember);
    }

    logOut() {
        from(signOut())
            .pipe(first())
            .subscribe({
                // Navigate to the login page whether or not sign-out succeeds;
                // a failed sign-out should never strand the user on a secure
                // page.
                next: () => this.router.navigate(['/login']),
                error: () => this.router.navigate(['/login']),
            });
    }

    completeNewPassword(newPassword: string) {
        return confirmSignIn({ challengeResponse: newPassword });
    }

    requestResetPasswordVerificationCode(username: string) {
        return resetPassword({ username });
    }

    confirmForgotPassword(code: string, username: string, newPassword: string) {
        return confirmResetPassword({
            username,
            confirmationCode: code,
            newPassword,
        });
    }

    get isLoggedIn(): Promise<boolean> {
        return fetchAuthSession()
            .then((session) => {
                return session.tokens !== undefined;
            })
            .catch((error) => {
                console.warn(error);
                return false;
            });
    }

    get userInfo(): Promise<any> {
        return fetchUserAttributes();
    }
}

/**
 * Where to go after signing in, given the login page's `returnUrl`. In-app
 * paths only: anything absolute, protocol-relative or pointing back at the
 * login page falls back to the default landing page.
 */
export const returnUrlAfterLogin = (returnUrl: string | null): string =>
    returnUrl && /^\/(?!\/)/.test(returnUrl) && !/^\/login\b/.test(returnUrl)
        ? returnUrl
        : '/';

export const authGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const isLoginForm = route.routeConfig?.path === 'login';
    return authService.isLoggedIn.then((isLoggedIn: boolean) => {
        if (isLoggedIn && isLoginForm) {
            router.navigate(['/']);
            return false;
        }
        if (!isLoggedIn && !isLoginForm) {
            // Carry the requested page through sign-in. An embedded frame
            // (EmbededModeService) signs in inside the frame, and landing on
            // the default All Orders page there would strand it.
            router.navigate(['/login'], {
                queryParams: { returnUrl: state.url },
            });
        }
        return isLoggedIn || isLoginForm;
    });
};
